/**
 * usePraticheList — F-005 Phase 4 (IMPL-P4-005).
 *
 * Polling hook per la lista delle pratiche cliente in corso. Usa TanStack
 * Query con refetch ogni 20s (BR-6 ≤ 30s); ferma il polling quando TUTTI
 * gli item sono in stato terminale (COMPLETATA / ANNULLATA / ARCHIVIATA).
 *
 * Pattern (cfr. INTRODUCTION.md Pattern Testing 3 + features/operatore/hooks):
 *   - queryKey array (`['pratiche-cliente', 'list', scope, page, pageSize]`)
 *     per invalidation granulare in Phase 5 (post-correzione).
 *   - `refetchInterval` callback form per leggere `query.state.data` e
 *     decidere se fermarsi.
 */
import { useQuery } from '@tanstack/react-query';
import {
  fetchPratiche,
  type PraticaListItem,
} from '../lib/cliente-pratiche-client';
import { isTerminalStato } from '../lib/stato-meta';

const LIST_REFETCH_INTERVAL_MS = 20_000;

export function usePraticheList(
  params: { scope?: 'in-progress'; page?: number; pageSize?: number } = {},
) {
  const scope = params.scope ?? 'in-progress';
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  return useQuery({
    queryKey: ['pratiche-cliente', 'list', scope, page, pageSize],
    queryFn: () => fetchPratiche({ scope, page, pageSize }),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return LIST_REFETCH_INTERVAL_MS;
      // Ferma il polling solo quando TUTTI gli item sono terminali.
      const allTerminal = data.items.every((item: PraticaListItem) =>
        isTerminalStato(item.stato),
      );
      return allTerminal ? false : LIST_REFETCH_INTERVAL_MS;
    },
  });
}
